import { User } from 'oidc-client-ts';
import { createApiClient } from '../oidc-api';
import { API_BASE_URL } from '../../layout/MainLayout';
import {
  HealthDeviceAuthRequestDto,
  HealthDeviceAuthResponseDto,
  HealthDeviceResponseDto,
  // ListUserChatMessageDto, // Não está sendo usado
  ListUserDto,
  // PaginatedResponseDto, // Não está sendo usado
  // PaginatedChatMessageResponseDto // Não está sendo usado,
  PaginatedResponseUserChatMessageDto,
  PaginatedResponseScheduleActionDto,
  ScheduleActionDto,
  UserDto,
  UserStatus
} from './types';
import { withRetry } from '../applications/applicationsService';

/**
 * Get list of users with pagination and optional filters
 */
export const getUsers = async (
  user: User | null,
  pageNumber: number = 1, 
  pageSize: number = 10,
  status?: UserStatus,
  username?: string,
  applicationId?: string,
  email?: string
): Promise<ListUserDto> => {
  const api = createApiClient(user);
  let url = `/v1/users?page_number=${pageNumber}&page_size=${pageSize}`;
  
  if (status) url += `&status=${status}`;
  if (username) url += `&username=${encodeURIComponent(username)}`;
  if (applicationId) url += `&application_id=${applicationId}`;
  if (email) url += `&email=${encodeURIComponent(email)}`;
  
  // Use retry for listing users which is a critical operation
  return withRetry(() => api.get<ListUserDto>(url));
};

/**
 * Get users by application ID
 */
export const getUsersByApplication = async (
  user: User | null,
  applicationId: string,
  page: number = 1,
  pageSize: number = 10
): Promise<ListUserDto> => {
  const api = createApiClient(user);
  
  // Use the application_id query parameter approach for now
  // This can be changed to a dedicated endpoint when available
  const url = `/v1/users?application_id=${applicationId}&page_number=${page}&page_size=${pageSize}`;
  
  console.log('getUsersByApplication URL:', url);
  
  return withRetry(() => api.get<ListUserDto>(url));
};

/**
 * Get list of user status options from API
 */
export const getUserStatuses = async (
  user: User | null
): Promise<string[]> => {
  const api = createApiClient(user);
  try {
    console.log("Fetching user statuses from API...");
    const statuses = await withRetry(() => api.get<string[]>(`/v1/user-status`));
    
    console.log("User statuses fetched successfully:", statuses);
    
    if (Array.isArray(statuses)) {
      return statuses;
    } else {
      console.warn("API returned unexpected format for user statuses:", statuses);
      throw new Error("Invalid user statuses format");
    }
  } catch (error) {
    console.error("Error fetching user statuses:", error);
    // Return fallback status values for development/testing
    return [
      "ACTIVATED",
      "INACTIVATED",
      "BLOCKED",
      "RECONFIGURE", 
      "ONBOARDING_IN_PROGRESS",
      "TICKET_OPENING"
    ];
  }
};

/**
 * Get user by ID
 * Using retry mechanism for more reliable fetching of individual user details
 */
export const getUserById = async (
  user: User | null,
  id: string
): Promise<UserDto> => {
  const api = createApiClient(user);
  return withRetry(() => api.get<UserDto>(`/v1/users/${id}`));
};

/**
 * Get user chat messages
 * Using retry for reliable message history retrieval
 * API returns messages in PaginatedResponseUserChatMessageDto format (as per Swagger)
 *
 * @param user - The authenticated user
 * @param userId - The ID of the user whose chat messages to retrieve
 * @param pageNumber - Page number (1-based)
 * @param pageSize - Number of messages per page
 * @param sortBy - Field to sort by (default: sequence_id)
 * @param ascending - Sort direction (default: false = descending)
 * @returns A paginated response containing chat messages
 */
export const getUserChatMessages = async (
  user: User | null,
  userId: string,
  pageNumber: number = 1,
  pageSize: number = 10,
  sortBy: string = 'sequence_id',
  ascending: boolean = false
): Promise<PaginatedResponseUserChatMessageDto> => {
  const api = createApiClient(user);
  const url = `/v1/users/${userId}/chat_messages?page_number=${pageNumber}&page_size=${pageSize}&sort_by=${sortBy}&ascending=${ascending}`;
  return withRetry(() => api.get<PaginatedResponseUserChatMessageDto>(url));
};

/**
 * Get user schedules
 * Using retry for reliable schedule retrieval
 */
export const getUserSchedules = async (
  user: User | null,
  userId: string,
  pageNumber: number = 1,
  pageSize: number = 10
): Promise<PaginatedResponseScheduleActionDto> => {
  const api = createApiClient(user);
  const url = `/v1/users/${userId}/schedules?page_number=${pageNumber}&page_size=${pageSize}`;
  return withRetry(() => api.get<PaginatedResponseScheduleActionDto>(url));
};

/**
 * Create user schedules
 * This is a state-changing operation, so we don't use retry to avoid duplicate creations
 */
export const createUserSchedules = async (
  user: User | null,
  userId: string,
  schedules: ScheduleActionDto[]
): Promise<ScheduleActionDto[]> => {
  const api = createApiClient(user);
  return api.post<ScheduleActionDto[]>(`/v1/users/${userId}/schedules`, schedules);
};

/**
 * Update user schedule
 * This is a state-changing operation, so we don't use retry to avoid duplicate updates
 */
export const updateUserSchedule = async (
  user: User | null,
  userId: string,
  scheduleId: string,
  schedule: ScheduleActionDto
): Promise<ScheduleActionDto> => {
  const api = createApiClient(user);
  return api.put<ScheduleActionDto>(`/v1/users/${userId}/schedules/${scheduleId}`, schedule);
};

/**
 * Delete user schedule
 */
export const deleteUserSchedule = async (
  user: User | null,
  userId: string,
  scheduleId: string
): Promise<void> => {
  const api = createApiClient(user);
  return api.delete<void>(`/v1/users/${userId}/schedules/${scheduleId}`);
};

/**
 * Get user health devices
 * Using retry for reliable health device listing
 */
export const getUserHealthDevices = async (
  user: User | null,
  userId: string,
  enabled: boolean = true,
  pageNumber: number = 1,
  pageSize: number = 10
): Promise<HealthDeviceResponseDto[]> => {
  const api = createApiClient(user);
  const url = `/v1/users/${userId}/health_devices?enabled=${enabled}&page_number=${pageNumber}&page_size=${pageSize}`;
  return withRetry(() => api.get<HealthDeviceResponseDto[]>(url));
};

/**
 * Get specific user health device
 * Using retry for reliable health device retrieval
 */
export const getUserHealthDevice = async (
  user: User | null,
  userId: string,
  healthDeviceId: string
): Promise<HealthDeviceResponseDto> => {
  const api = createApiClient(user);
  const url = `/v1/users/${userId}/health_devices/${healthDeviceId}`;
  return withRetry(() => api.get<HealthDeviceResponseDto>(url));
};

/**
 * Create user health device
 * Using direct fetch to ensure proper Content-Type header
 */
export const createUserHealthDevice = async (
  user: User | null,
  userId: string,
  deviceData: HealthDeviceAuthRequestDto
): Promise<HealthDeviceAuthResponseDto> => {
  if (!user || !user.access_token) {
    throw new Error('Usuário não autenticado ou token não disponível');
  }

  const url = `${API_BASE_URL}/v1/users/${userId}/health_devices`;

  // Define explicit headers with Content-Type
  const headers = {
    'Authorization': `Bearer ${user.access_token}`,
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };

  // Log para debug - remover em produção
  console.log(`Direct fetch to ${url} [POST]:`, {
    headers: JSON.stringify(headers),
    body: JSON.stringify(deviceData)
  });

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(deviceData),
    credentials: 'omit' // Important for AWS API Gateway
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage: string;

    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.message || errorData.error || `${response.status}: ${response.statusText}`;
    } catch (e) {
      errorMessage = errorText || `${response.status}: ${response.statusText}`;
    }

    throw new Error(errorMessage);
  }

  return await response.json();
};

/**
 * Activate user health device
 * Using direct fetch to ensure proper Content-Type header
 */
export const activateUserHealthDevice = async (
  user: User | null,
  userId: string,
  healthDeviceId: string
): Promise<string> => {
  if (!user || !user.access_token) {
    throw new Error('Usuário não autenticado ou token não disponível');
  }

  const url = `${API_BASE_URL}/v1/users/${userId}/health_devices/${healthDeviceId}/activate`;

  // Define explicit headers with Content-Type
  const headers = {
    'Authorization': `Bearer ${user.access_token}`,
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };

  // Log para debug - remover em produção
  console.log(`Direct fetch to ${url} [POST]:`, {
    headers: JSON.stringify(headers),
    body: JSON.stringify({})
  });

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({}), // Send empty object, not undefined
    credentials: 'omit' // Important for AWS API Gateway
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage: string;

    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.message || errorData.error || `${response.status}: ${response.statusText}`;
    } catch (e) {
      errorMessage = errorText || `${response.status}: ${response.statusText}`;
    }

    throw new Error(errorMessage);
  }

  if (response.status === 204) {
    return "";
  }

  const result = await response.text();
  return result || "";
};

/**
 * Deactivate user health device
 * Using direct fetch to ensure proper Content-Type header
 */
export const deactivateUserHealthDevice = async (
  user: User | null,
  userId: string,
  healthDeviceId: string
): Promise<string> => {
  if (!user || !user.access_token) {
    throw new Error('Usuário não autenticado ou token não disponível');
  }

  const url = `${API_BASE_URL}/v1/users/${userId}/health_devices/${healthDeviceId}/deactivate`;

  // Define explicit headers with Content-Type
  const headers = {
    'Authorization': `Bearer ${user.access_token}`,
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };

  // Log para debug - remover em produção
  console.log(`Direct fetch to ${url} [POST]:`, {
    headers: JSON.stringify(headers),
    body: JSON.stringify({})
  });

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({}), // Send empty object, not undefined
    credentials: 'omit' // Important for AWS API Gateway
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage: string;

    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.message || errorData.error || `${response.status}: ${response.statusText}`;
    } catch (e) {
      errorMessage = errorText || `${response.status}: ${response.statusText}`;
    }

    throw new Error(errorMessage);
  }

  if (response.status === 204) {
    return "";
  }

  const result = await response.text();
  return result || "";
};